import {
  ChangeDetectionStrategy, Component, effect, inject, signal
} from '@angular/core';
import {
  Router, RouterLink, RouterLinkActive, RouterOutlet, NavigationEnd
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  selectAppStateUserProfileEmail,
  selectAppStateUserProfileName,
  selectAppStateUserProfileRole
} from '../../shared/provider/app-state-provider/state/state.selectors';
import { adminLogout } from '../../core/auth/store/auth.actions';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AdminBreadcrumbService, buildAdminBreadcrumbs } from './admin-breadcrumb.service';

export interface Language {
  code: string;
  label: string;
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    AsyncPipe,
    NgOptimizedImage,
    Breadcrumb,
    ButtonModule,
    DialogModule,
  ],
  templateUrl: './admin-layout.html',
  styleUrl: './admin-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminLayoutComponent {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly adminBreadcrumbService = inject(AdminBreadcrumbService);

  readonly profileName$ = this.store.select(selectAppStateUserProfileName);
  readonly profileEmail$ = this.store.select(selectAppStateUserProfileEmail);
  readonly profileRole$ = this.store.select(selectAppStateUserProfileRole);

  readonly breadcrumbs = signal<MenuItem[]>([]);
  readonly showLogoutConfirm = signal(false);

  readonly languages: Language[] = [
    {
      code: 'en',
      label: 'English',
    },
    {
      code: 'es',
      label: 'Español',
    },
    {
      code: 'fr',
      label: 'Français',
    },
  ];

  constructor() {
    effect(() => {
      this.breadcrumbs.set(
        buildAdminBreadcrumbs(this.router.url, this.adminBreadcrumbService.pageLabel())
      );
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.breadcrumbs.set(
          buildAdminBreadcrumbs(event.urlAfterRedirects, this.adminBreadcrumbService.pageLabel())
        );
      });
  }

  get currentLang(): string {
    return this.translate.currentLang() ?? 'en';
  }

  setLanguage(code: string): void {
    this.translate.use(code);
  }

  navigateToUserProfile(): void {
    void this.router.navigate(['/admin/profile']);
  }

  logout(): void {
    this.showLogoutConfirm.set(true);
  }

  confirmLogout(): void {
    this.showLogoutConfirm.set(false);
    this.store.dispatch(adminLogout());
  }

  cancelLogout(): void {
    this.showLogoutConfirm.set(false);
  }
}
